import { type UserRole, setUser } from '@/features/auth/authSlice';
import { api, updateSessionCache } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { Organizer } from '@eventflow/shared-types';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface LoginForm {
  email: string;
  password: string;
}

// Mensajes de error amigables para el usuario
const ERROR_MESSAGES: Record<string, string> = {
  'Invalid login credentials': 'Credenciales incorrectas. Verifica tu email y contraseña.',
  'Email not confirmed': 'Tu email no ha sido confirmado. Revisa tu bandeja de entrada.',
  'Too many requests': 'Demasiados intentos. Espera unos minutos antes de intentar de nuevo.',
  'Network request failed': 'Error de conexión. Verifica tu conexión a internet.',
  'Failed to fetch': 'No se pudo conectar al servidor. Intenta de nuevo.',
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Buscar mensaje amigable
    for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.includes(key)) {
        return message;
      }
    }
    return error.message;
  }
  return 'Error al iniciar sesión. Intenta de nuevo.';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    isAuthenticated,
    roles,
    isLoading: isAuthLoading,
  } = useAppSelector((state) => state.auth);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>();

  // Función para determinar la ruta de redirección según roles
  const getRedirectPath = useCallback(
    (userRoles: string[]): string => {
      // Obtener ruta de retorno si existe
      const from = (location.state as { from?: string })?.from;

      if (userRoles.includes('superadmin')) {
        // Superadmin va al dashboard principal
        return from?.startsWith('/dashboard') ? from : '/dashboard';
      }

      if (userRoles.includes('organizer')) {
        // Organizador va a sus eventos
        return from?.startsWith('/dashboard') ? from : '/dashboard/events';
      }

      // Cliente va a eventos públicos o ruta de retorno
      return from || '/eventos';
    },
    [location.state]
  );

  // Redirigir si el usuario ya está autenticado
  useEffect(() => {
    if (isAuthenticated && roles.length > 0 && !isRedirecting) {
      const redirectPath = getRedirectPath(roles);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, roles, navigate, getRedirectPath, isRedirecting]);

  const onSubmit = async (data: LoginForm) => {
    // Validación adicional antes de enviar
    if (!data.email?.trim() || !data.password?.trim()) {
      setError('Por favor completa todos los campos.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // 1. Autenticar con Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });

      if (authError) {
        throw authError;
      }

      if (!authData.session || !authData.user) {
        throw new Error('No se pudo establecer la sesión');
      }

      // 2. Actualizar caché de sesión inmediatamente
      updateSessionCache(authData.session);

      // 3. Obtener roles del usuario
      let userRoles: UserRole[] = ['client'];
      try {
        const rolesResp = await api.get<{ roles: Array<{ role_name: string }> }>(
          `/users/${authData.user.id}/roles`
        );
        userRoles = rolesResp.roles.map((r) => r.role_name) as UserRole[];
      } catch {
        // Use default roles
      }

      // 4. Obtener perfil de organizador solo si es organizer Y NO es superadmin
      // (superadmin tiene acceso total sin necesidad de perfil)
      let organizer: Organizer | null = null;
      const isSuperadmin = userRoles.includes('superadmin');
      const isOrganizer = userRoles.includes('organizer');

      if (isOrganizer && !isSuperadmin) {
        try {
          organizer = await api.get<Organizer>('/auth/profile');
        } catch {
          // Profile not available
        }
      }

      // 5. Actualizar estado global ANTES de navegar (evita race conditions)
      dispatch(
        setUser({
          user: authData.user,
          organizer,
          roles: userRoles,
        })
      );

      // 6. Marcar que estamos redirigiendo para evitar doble navegación
      setIsRedirecting(true);

      // 7. Determinar ruta y navegar
      const redirectPath = getRedirectPath(userRoles);

      // Pequeño delay para asegurar que el estado se propagó
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 100);
    } catch (err) {
      // No logueamos como error porque credenciales incorrectas es un flujo esperado
      setError(getErrorMessage(err));

      // Limpiar campo de contraseña en error
      setValue('password', '');

      setIsLoading(false);
      setIsRedirecting(false);
    }
  };

  // Mostrar loading mientras se verifica autenticación inicial
  if (isAuthLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-500">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Si está redirigiendo, mostrar loading
  if (isRedirecting) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <p className="text-sm text-gray-500">Iniciando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="card">
          <h1 className="text-3xl font-bold text-center mb-6">Iniciar Sesión</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                autoComplete="email"
                autoFocus
                className={`input-field ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                disabled={isLoading}
                {...register('email', {
                  required: 'El email es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido',
                  },
                })}
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Contraseña</label>
              <input
                type="password"
                autoComplete="current-password"
                className={`input-field ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                disabled={isLoading}
                {...register('password', {
                  required: 'La contraseña es requerida',
                  minLength: {
                    value: 6,
                    message: 'La contraseña debe tener al menos 6 caracteres',
                  },
                })}
              />
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <p className="text-center mt-4 text-sm text-gray-600">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-blue-600 hover:underline font-medium">
              Regístrate aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
